import {
  ContentSwitcher,
  Field,
  IconButton,
  SingleSelectDropdown,
  Switch,
  Text,
  TextInput,
  type ContentSwitcherOption,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  CHANNEL_SELECTION_TOPICS,
  EXPERIMENT_NOTE_MAX_LENGTH,
  selectedChannelCount,
  SESSION_DISPLAY_NAME_MAX_LENGTH,
  type BranchCommitView,
  type ChannelSelection,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import {
  intervalLabel,
  resolveScheduleTimezone,
  scheduleIntervalSeconds,
  scheduleMinFreeGpus,
  type ScheduleDraft,
} from "./scheduleDraft";
import { CopyButton } from "@/components/buttons";
import { BranchHashCell, shortSha } from "@/components/table/branchHash";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { channelTopicShortLabel } from "@/components/dataset";
import { Editor, type EditorBinding } from "@/components/editor";
import { HatTextModal } from "@/components/modal";
import { EvalStartSummary } from "@/page/training/evalParams/EvalStartSummary";
import { EvaluationParametersPanel } from "@/page/training/evalParams/EvaluationParametersPanel";
import { CodeDiffViewer, CodeEditor } from "@/components/codeEditor";
import { ChannelSelectionEditor } from "./ChannelSelectionEditor";
import { ValidationReportPanel } from "./ValidationReportPanel";
import { buildConfigRenderModel } from "./configRenderModel";
import { CONFIGURE_PANEL_ID, ConfigureSectionNav, sectionTabId } from "./ConfigureSectionNav";
import type { ConfigureSection } from "./configureSections";
import { CreateExperimentDialog } from "./CreateExperimentDialog";
import { SubmitField, ToggleField } from "./submitFields";
import { describeCronExpression } from "@/modules/training/public";
import { EmptyState, stateFrameClassName } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { DatasetPicker } from "@/components/dataset/selection/DatasetPicker";
import { InitStrategyPanel } from "./InitStrategyPanel";
import type { MultiSelectListMode } from "@/components/dataset/selection/MultiSelectList";
import { annotationSetLabel, type AnnotationSetsSource } from "./annotationSetSelectable";
import { SelectedDatasetList } from "./SelectedDatasetList";
import { DatasetPreviewPanel } from "@/components/dataset/selection/DatasetPreviewPanel";
import { KeyValueEditor } from "./KeyValueEditor";
import {
  BATCH_SIZE_OVERRIDE_KEY,
  computeAutoLr,
  hasRandomBackgroundTransform,
  parseOverrideKey,
} from "./recipe";
import { RecipeOverrideEditor } from "./RecipeOverrideEditor";
import { hasScheduleStep } from "./submitWizardFeatures";
import {
  activeConfigYaml,
  ALLOW_SPOT_OPTIONS,
  codeSourceParts,
  allowSpotChoiceHint,
  allowSpotChoiceLabel,
  allowSpotChoiceValue,
  allowSpotFromChoiceValue,
  buildResumeOnRetry,
  buildTargetDatasetIndex,
  configEditError,
  hasConfigEdit,
  isDirectConfigEdit,
  isManagedExtraKey,
  learningCoreRef,
  MIN_LIFETIME_OPTIONS,
  minLifetimeLabel,
  type KeyValuePair,
  type SubmitDatasetSelection,
  type SubmitFormState,
  type SubmitInitState,
} from "./submitWizardForm";
import {
  BRANCH_COMMITS_SINCE_LIMIT,
  experimentNoteChanged,
  type SubmitWizardController,
} from "./useSubmitWizardController";
import styles from "./submitWizard.module.css";
import selectionStyles from "@/components/dataset/selection/DatasetSelection.module.css";
import { storageBackendGateFor } from "@/api/datasetStorageBackend";

type StepProps = { controller: SubmitWizardController };

// diff 에디터 자리의 대기·빈 상태 틀. 에디터와 같은 클래스로 열의 남은 높이를 받는다.
const DIFF_STATE_FRAME = `${styles.configEditorShell} ${styles.configEditorFill} ${stateFrameClassName}`;

// 커서에서 미리보기까지의 디바운스. 미리보기 한 번이 상세·구조·메모·에피소드 요청을 함께 보내므로,
// 목록을 훑는 동안에는 요청하지 않고 멈춘 항목만 로드한다.
const PREVIEW_DEBOUNCE_MS = 200;

// 스팟 세그먼트. 상태와 문구는 폼 모듈이 소유하고, 여기서는 컨트롤 형태로만 바꾼다.
const allowSpotOptions: ContentSwitcherOption[] = ALLOW_SPOT_OPTIONS.map(
  ({ value, label, hint }) => ({
    value,
    label,
    tooltip: hint,
  }),
);

// 데이터셋 선택 방식. 몇 개를 담을지 정하는 화면 축이다. 학습 시작점(init: scratch/
// finetune/retrain)은 구성 스텝의 **별도 섹션**이 소유하고, 이 세그먼트는 바꾸지 않는다.
// 두 축이 서로 독립이라 라벨에도 서로를 섞어 적지 않는다(ZERG-294). 다중을 골라도 1개만 담고
// 넘어갈 수 있다(강제하지 않는다).
const SELECTION_MODE_OPTIONS: ContentSwitcherOption[] = [
  { value: "single", label: "단일 Dataset", tooltip: "Dataset 하나로 학습합니다." },
  {
    value: "multi",
    label: "멀티 Dataset",
    tooltip: "여러 Dataset을 담아 한 배치로 섞어 학습합니다. Training 방식과는 무관합니다.",
  },
];

// 선택 방식 세그먼트는 패널 폭을 채우고 두 세그먼트가 균등하게 나눈다. 첫 화면에서 무엇을 고르는
// 자리인지 먼저 읽혀야 한다. 출처 세그먼트는 그 아래에서 내용 폭을 유지해 위계를 구분한다.
const SELECTION_MODE_WIDTH = { type: "expanded" } as const;

// ① 데이터셋 선택. 왼쪽은 담을 목록(picker), 오른쪽은 커서 항목 미리보기다. 이 스텝은 학습 데이터만
// 묻는다. 담을 곳(워크스페이스·실험)과 순서·role·weight 편집은 구성 스텝이 소유한다.
export function DatasetSelectStep({ controller }: StepProps): ReactNode {
  const { form, setDatasets } = controller;
  // 미리보기 커서. 목록에서 마지막으로 훑은(클릭·포커스 이동) 항목이다. 담김 여부와 관계없어서
  // 담기 전에도 내용을 확인할 수 있다.
  const [previewTarget, setPreviewTarget] = useState<{ uuid: string; name: string } | null>(null);
  const previewTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (previewTimerRef.current !== null) {
        window.clearTimeout(previewTimerRef.current);
      }
    },
    [],
  );
  function schedulePreview(item: { uuid: string; name: string }): void {
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = window.setTimeout(() => setPreviewTarget(item), PREVIEW_DEBOUNCE_MS);
  }
  // 선택 방식. 스텝을 오갈 때 이 컴포넌트가 언마운트되므로 폼에서 복원한다. 2개 이상 담겼으면
  // 다중으로 담은 것이다. 1개 이하는 기본으로 단일로 열고, 세그먼트 한 번으로 바꿀 수 있다.
  const [selectionMode, setSelectionMode] = useState<MultiSelectListMode>(
    form.datasets.length > 1 ? "multi" : "single",
  );
  const selectedUuids = new Set(form.datasets.map((dataset) => dataset.uuid));
  // 커서가 아직 없으면 담은 항목 중 첫 학습 입력을 대표로 표시한다.
  const fallbackDataset =
    form.datasets.find((dataset) => dataset.role === "train") ?? form.datasets[0] ?? null;
  const previewDataset: SubmitDatasetSelection | null = previewTarget
    ? {
        uuid: previewTarget.uuid,
        label: previewTarget.name,
        role: "train",
        weight: null,
        annotationSetUuid: null,
      }
    : fallbackDataset;

  // 단일에서는 고른 항목 하나로 교체한다. 같은 행을 다시 눌러도 비우지 않는다. 체크박스가 없어서
  // '해제'할 대상이 없고, 비면 다음 스텝이 잠긴다.
  // 다중에서는 같은 데이터셋을 다시 누르면 뺀다. 담을 때는 뒤에 추가해 순서(=학습 타깃 인덱스)를 유지한다.
  function toggleDataset(selection: SubmitDatasetSelection): void {
    if (selectionMode === "single") {
      setDatasets([selection]);
      return;
    }
    const exists = form.datasets.some((dataset) => dataset.uuid === selection.uuid);
    setDatasets(
      exists
        ? form.datasets.filter((dataset) => dataset.uuid !== selection.uuid)
        : [...form.datasets, selection],
    );
  }

  // 단일로 돌아오면 첫 번째만 남긴다. 체크박스가 없는 화면에 여러 개가 담긴 중간 상태를
  // 두지 않는다. 다중으로 바꿀 때는 담은 항목을 그대로 유지한다.
  function changeSelectionMode(mode: MultiSelectListMode): void {
    setSelectionMode(mode);
    if (mode === "single") {
      setDatasets(form.datasets.slice(0, 1));
    }
  }
  return (
    <div className={selectionStyles.datasetStep}>
      {/* 왼쪽 컬럼. 담을 목록(선택 방식 + 출처 스위처 + 검색 + 목록)이다. */}
      <div className={selectionStyles.datasetPickerCol}>
        {/* 데이터셋 선택 패널. 선택 방식·출처·검색·목록이 한 컴포넌트로 읽히도록 배경 면을 둔다.
            여기서는 담은 수만 표시한다. 순서·role·weight 편집 표는 구성 스텝이 소유한다. */}
        <div className={selectionStyles.pickerPanel}>
          <ContentSwitcher
            label="Dataset 선택 방식"
            size="md"
            width={SELECTION_MODE_WIDTH}
            options={SELECTION_MODE_OPTIONS}
            value={selectionMode}
            onValueChange={(value) => changeSelectionMode(value === "multi" ? "multi" : "single")}
          />
          <DatasetPicker
            selectedUuids={selectedUuids}
            onToggle={(selection) =>
              toggleDataset({ ...selection, role: "train", weight: null, annotationSetUuid: null })
            }
            onPreview={schedulePreview}
            selectionMode={selectionMode}
            countLabel={`${form.datasets.length}개 담음`}
            // 환경 한정이 있는 위저드는 목록 단계에서 좁힌다. 예를 들어 스케줄은 eval 추종과 Isaac
            // Evaluation 이 전제라 simulation Dataset 만 받는다. 고른 뒤에 거절되는 경우를 만들지 않는다.
            environment={controller.features.datasetEnvironment ?? undefined}
            // 학습을 지원하지 않는 저장 위치의 Dataset 은 제출이 409 다. 그래서 후보에서 뺀다.
            storageBackend={storageBackendGateFor("training") ?? undefined}
          />
        </div>
      </div>

      {/* 오른쪽 컬럼. 커서 항목의 핵심 정보, 메모, 미리보기(에피소드 목록 + 영상)를 표시한다.
          미리보기는 선택과 분리된 축이다. 목록에서 훑는 항목을 그대로 따라간다. */}
      <div className={selectionStyles.datasetPreviewCol}>
        {previewDataset ? (
          /* 이름·라벨 칩은 패널의 상세 접기 헤더가 소유한다. */
          <DatasetPreviewPanel key={previewDataset.uuid} selection={previewDataset} />
        ) : (
          <EmptyState size="lg">목록에서 Dataset을 클릭하면 정보·미리보기가 표시됩니다.</EmptyState>
        )}
      </div>
    </div>
  );
}

// 세션을 담을 곳(실험), 세션 이름, 실험 노트. 워크스페이스는 admin 이 책임지는 개념이라
// session 모드 화면에 없다(docs/rules.md §15). 컨트롤러가 내 워크스페이스를 자동으로 고른다.
// schedule 모드는 admin 전용이고 대상이 그룹 공용 Workspace 라서 여기서 고른다.
function ExperimentControls({ controller }: StepProps): ReactNode {
  const {
    form,
    features,
    workspacesState,
    selectWorkspace,
    experimentsState,
    experimentDialog,
    selectExperiment,
    openCreateExperiment,
    closeCreateExperiment,
    submitCreateExperiment,
    updateForm,
  } = controller;
  const experiments = experimentsState.status === "success" ? experimentsState.data : [];
  const experimentOptions = [
    { value: "", label: "실험 선택 또는 신규 실험" },
    ...experiments.map((experiment) => ({ value: experiment.id, label: experiment.name })),
  ];
  // 실험 목록은 내 학습 환경이 정해진 뒤에만 표시된다. 그 앞 단계가 로딩 중이거나 실패했다는
  // 사실은 여기서만 보이므로(실험 드롭다운이 이유 없이 비어 보인다) 진단을 그대로 표시한다.
  const scopeLoading = workspacesState.status === "idle" || workspacesState.status === "loading";
  const scopeError = workspacesState.status === "error" ? workspacesState.message : null;
  const workspaceOptions =
    workspacesState.status === "success"
      ? workspacesState.data.map((workspace) => ({ value: workspace.id, label: workspace.name }))
      : [];

  return (
    <>
      {features.ownership === "shared" ? (
        <SubmitField
          label="공용 Workspace"
          required
          hint="Schedule은 이 Workspace에서 실행됩니다. 목록은 Schedule 페이지의 [그룹 공용 Workspace 관리]에서 정합니다."
        >
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.default}
            label="공용 Workspace"
            className={styles.fullDropdown}
            value={form.workspaceId}
            options={workspaceOptions}
            placeholder={scopeLoading ? "불러오고 있습니다." : "공용 Workspace를 고르세요."}
            disabled={scopeLoading || scopeError !== null}
            onValueChange={selectWorkspace}
          />
        </SubmitField>
      ) : null}
      <SubmitField
        label="Training 실험 이름"
        required
        dividerTop={features.ownership === "shared"}
        hint={
          features.ownership === "shared"
            ? "공용 Workspace의 기존 실험은 아직 목록에 나오지 않습니다. [+ 새 실험]으로 만들어 주세요."
            : undefined
        }
      >
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="Training 실험 이름"
          className={styles.fullDropdown}
          value={form.experimentId}
          options={experimentOptions}
          onValueChange={selectExperiment}
          disabled={scopeError !== null}
          searchable
          searchPlaceholder="실험을 검색해 보세요."
          searchAction={
            <IconButton
              text="+ 새 실험"
              size="sm"
              variant="surface"
              onClick={openCreateExperiment}
            />
          }
        />
        {scopeLoading || experimentsState.status === "loading" ? (
          <LoadingState size="sm">실험을 불러오고 있습니다.</LoadingState>
        ) : null}
        {scopeError !== null ? <p className={styles.errorText}>{scopeError}</p> : null}
        {experimentsState.status === "error" ? (
          <p className={styles.errorText}>{experimentsState.message}</p>
        ) : null}
      </SubmitField>
      <SubmitField label="Training Session 이름" dividerTop>
        {/* SubmitField 의 제목은 표시용 Text 라 id 연결이 없다. 접근 이름은 컨트롤이 직접 가진다.
            비웠을 때의 결과는 입력 자리 안에 표시한다. 사용자가 빈 칸을 볼 때 바로 읽는다. */}
        <TextInput
          value={form.displayName}
          maxLength={SESSION_DISPLAY_NAME_MAX_LENGTH}
          placeholder="이 Session을 어떻게 부를지 정해 주세요. 비워 두면 서버가 이름을 정합니다."
          aria-label="Training Session 이름"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            updateForm({ displayName: event.target.value })
          }
        />
      </SubmitField>
      <ExperimentNoteEditor controller={controller} />
      {experimentDialog ? (
        <CreateExperimentDialog
          submitting={experimentDialog.submitting}
          errorMessage={experimentDialog.error}
          onClose={closeCreateExperiment}
          onSubmit={submitCreateExperiment}
        />
      ) : null}
    </>
  );
}

// 고른 실험의 연구 노트(training `Experiment.description`). 상시 편집 textarea 와 명시 저장을 쓴다.
// 실험은 여러 세션이 공유하는 단위라 타이핑을 바로 보내지 않는다. 서버 값과 다를 때만
// 저장·되돌리기가 켜진다(세션 노트 NotePanel 과 같은 방식). 저장하지 않고 제출하면 컨트롤러의
// submit 이 세션보다 먼저 노트를 쓴다. 실험을 고르기 전에도 자리를 유지해 폼 높이가 바뀌지 않게 한다.
// c6 에 멀티라인 입력이 없어서 native textarea 를 쓴다(앱 관례).
function ExperimentNoteEditor({ controller }: StepProps): ReactNode {
  const {
    form,
    experimentsState,
    experimentNote,
    beginEditExperimentNote,
    changeExperimentNote,
    cancelEditExperimentNote,
    saveExperimentNote,
  } = controller;
  const experiment =
    experimentsState.status === "success"
      ? experimentsState.data.find((item) => item.id === form.experimentId)
      : undefined;
  const selected = form.experimentId !== "" && experiment !== undefined;
  const savedNote = experiment?.description ?? null;
  const text = experimentNote?.text ?? savedNote ?? "";
  const saving = experimentNote?.saving ?? false;
  const changed = experimentNote !== null && experimentNoteChanged(experimentNote.text, savedNote);
  const noteEditor: EditorBinding = {
    value: text,
    inputDisabled: !selected || saving,
    setValue: (next) =>
      experimentNote === null ? beginEditExperimentNote(next) : changeExperimentNote(next),
  };
  return (
    <SubmitField label="실험 노트" dividerTop>
      <Editor
        controller={noteEditor}
        className={styles.noteInput}
        rows={4}
        maxLength={EXPERIMENT_NOTE_MAX_LENGTH}
        aria-label="실험 노트"
        placeholder={
          selected
            ? "이 실험의 목적이나 메모를 입력해 주세요."
            : "실험을 고르면 그 실험의 노트가 표시됩니다."
        }
      />
      {experimentNote?.error ? <p className={styles.errorText}>{experimentNote.error}</p> : null}
      {selected ? (
        <div className={styles.noteActions}>
          <span className={styles.hint}>
            {text.length}/{EXPERIMENT_NOTE_MAX_LENGTH}
          </span>
          <IconButton
            text="되돌리기"
            size="sm"
            variant="surface"
            disabled={!changed || saving}
            onClick={cancelEditExperimentNote}
          />
          <IconButton
            text={saving ? "저장 중…" : "저장"}
            size="sm"
            variant="solid"
            disabled={!changed || saving}
            onClick={() => void saveExperimentNote()}
          />
        </div>
      ) : null}
    </SubmitField>
  );
}

// config 적용 방식 두 가지. 폼의 `editedYaml` 유무가 곧 방식이라(isDirectConfigEdit) 별도 상태가 없다.
type ConfigApplyMode = "overrides" | "direct";

// 본문 직접 편집의 화면 상태 묶음. 초안과 전환·되돌리기 요청이다. 스텝이 가지고 config 섹션 본문이 받는다.
type ConfigEditProps = {
  // 커밋하지 않은 초안(없으면 null). 에디터 value 와 옆 열 diff 가 함께 읽는다.
  draft: string | null;
  onDraftChange: (next: string) => void;
  onDraftBlur: () => void;
  onModeChange: (mode: ConfigApplyMode) => void;
  onResetRequest: () => void;
  // 원본과 다른 본문이 있는지. 커밋하지 않은 초안도 센다. 버리는 동작의 확인과 되돌리기 버튼이 읽는다.
  dirty: boolean;
};

// 편집본을 버리는 두 동작. 확인 모달의 문구가 서로 다르다.
type ConfigEditDiscard = "leave" | "reset";

// ② 학습 구성. 내비(섹션 목록), 선택 섹션 상세, 옆 열로 구성한다. 섹션마다 필요한 폭이 크게 달라서
// (override 행 고정 폭, mux 토글 그룹, Policy 목록) 한 열에 모두 쌓지 않고 한 번에 하나만 펼친다.
//
// **옆 열의 내용은 두 섹션만 채운다.**
//   - 브랜치 및 config: 원본 대비 변경 diff(읽기 전용)를 표시한다.
//   - Isaac Evaluation: 평가가 보낼 값(이미지·실행 횟수·파라미터)을 확인하는 요약을 표시한다.
// 나머지 섹션에서는 그 자리를 비워 둔다. **열은 항상 셋이다**(사용자 확정). 섹션마다 열 수가
// 다르면 내비와 상세가 좌우로 움직이고, 섹션을 옮길 때마다 화면 배치가 바뀐다.
// 요약과 제출은 확정 스텝이 소유한다.
//
// config 는 overrides 와 본문 직접 편집 중 **한 방식만** 쓴다(사용자 확정). config 섹션 본문의
// 스위처(config 파일 바로 아래)가 방식을 고른다. 직접 편집의 에디터도 그 섹션 본문 안에
// 인라인으로 표시한다. 옆 열은 어느 방식이든 항상 결과(원본 대비 diff)만 표시한다.
//
// 본문 편집의 화면 상태(커밋하지 않은 초안·버리기 확인)는 **스텝이 가진다.** 에디터는 섹션 본문
// 안에 있어서 섹션을 옮기면 언마운트된다. 에디터가 상태를 가지면 입력하던 초안이 그때마다 알림
// 없이 사라진다. 폼이 아니라 여기 두는 이유는, 이 상태가 제출 payload 와 관계없는 위저드 수명의
// 화면 상태이기 때문이다.
export function ConfigureStep({ controller }: StepProps): ReactNode {
  const {
    form,
    configureSections: entries,
    configureSection,
    setConfigureSection,
    editConfigYaml,
    enterDirectEdit,
    leaveDirectEdit,
    resetDirectEditToOriginal,
  } = controller;
  // 컨트롤러가 active 를 항상 목록 안(잠기지 않은 항목)으로 되돌린다. 그래서 fallback 은 타입을
  // 좁히는 용도이고 실제로 실행되지 않는다.
  const active = entries.find((entry) => entry.section === configureSection) ?? entries[0];
  // 타이핑은 이 초안에만 쌓이고, 포커스를 잃을 때 폼에 한 번 반영한다. 폼이 바뀌면 파싱·diff·
  // 크기 검사가 연달아 실행된다. 이것을 글자마다 실행할 필요가 없다(YAML 왕복이 여러 번이다).
  const [draft, setDraft] = useState<string | null>(null);
  // 편집본을 버리기 전 확인. 기다리는 동작을 가진다(없으면 닫힘).
  const [pendingDiscard, setPendingDiscard] = useState<ConfigEditDiscard | null>(null);
  const activeYaml = activeConfigYaml(form.config);
  // 밖에서 본문이 바뀌면(원본 되돌리기·config 교체·커밋 반영) 초안을 버리고 폼 값을 따른다.
  useEffect(() => setDraft(null), [activeYaml]);
  const commitDraft = () => {
    if (draft !== null && draft !== activeYaml) {
      editConfigYaml(draft);
    }
  };
  // 섹션 이동(클릭·↑/↓ 모두)은 초안을 커밋한다. 에디터가 섹션 본문과 함께 언마운트되어
  // blur 가 오지 않으므로, 입력한 값을 잃지 않으려면 여기서 반영해야 한다.
  useEffect(() => {
    if (draft !== null && active.section !== "config") {
      commitDraft();
    }
    // commitDraft 는 렌더마다 새 함수라 의존성에 넣지 않는다. 섹션 이탈만 트리거다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.section]);
  // 원본과 다른 본문이 있는지. 커밋된 편집본과 커밋하지 않은 초안 중 화면에 보이는 쪽으로 판정한다.
  // 초안은 문자열 비교만 한다. 정규화 비교는 YAML 왕복이라 글자마다 실행하지 않는다. 그래서 공백만
  // 다른 초안을 편집으로 잘못 판정할 수 있고, 그 결과는 확인이 한 번 더 표시되는 것뿐이다.
  const dirty = draft !== null ? draft !== form.config.inlineYaml : hasConfigEdit(form.config);
  const configEdit: ConfigEditProps = {
    draft,
    onDraftChange: setDraft,
    onDraftBlur: commitDraft,
    // overrides → 직접 편집은 행을 본문에 반영하므로 잃는 값이 없다. 반대 방향은 편집본을 버리므로
    // 실제로 고친 내용이 있을 때만 확인을 요청한다.
    onModeChange: (mode) => {
      if (mode === "direct") {
        enterDirectEdit();
        return;
      }
      if (dirty) {
        setPendingDiscard("leave");
        return;
      }
      setDraft(null);
      leaveDirectEdit();
    },
    onResetRequest: () => setPendingDiscard("reset"),
    dirty,
  };
  // 확인. 초안을 먼저 비운다. 초안만 다르고 폼은 원본과 같으면 activeYaml 이 바뀌지 않아 초안이 남는다.
  const confirmDiscard = () => {
    const discard = pendingDiscard;
    setPendingDiscard(null);
    setDraft(null);
    if (discard === "leave") {
      leaveDirectEdit();
    } else if (discard === "reset") {
      resetDirectEditToOriginal();
    }
  };
  return (
    <div className={styles.configureFill}>
      <ConfigureSectionNav
        entries={entries}
        active={active.section}
        className={styles.configureNav}
        onSelect={setConfigureSection}
        codeSource={codeSourceParts(form, null)}
      />
      <div
        className={styles.configureDetail}
        id={CONFIGURE_PANEL_ID}
        role="tabpanel"
        aria-labelledby={sectionTabId(active.section)}
      >
        {/* 머리글은 고정이고 본문만 아래로 스크롤한다. 제목이 있어야 옆 열의 diff 머리글과 첫 줄이
            같은 높이에서 시작한다. 없으면 상세만 두 줄 위에서 시작해서 열끼리 어긋나 보인다.
            문구는 내비가 쓰는 라벨과 같다(같은 대상을 두 이름으로 부르지 않는다). */}
        <div className={styles.configureDetailHead}>
          <Text variant={hatcheryTextModes["title-bold"]}>{active.label}</Text>
        </div>
        <div className={styles.configureSectionBody}>
          <ConfigureSectionBody
            section={active.section}
            controller={controller}
            configEdit={configEdit}
          />
        </div>
      </div>
      {active.section === "evaluation" ? (
        // 요약은 자연 높이라 열이 스크롤을 소유한다.
        <div className={`${styles.configureBody} ${styles.configureBodyScroll}`}>
          <IsaacEvaluationSummaryBlock title="Isaac Evaluation 확인" controller={controller} />
        </div>
      ) : active.section === "config" ? (
        <ConfigDiffPane controller={controller} draft={draft} />
      ) : (
        // 채울 내용이 없어도 열과 구분자는 남긴다. 구분자가 생기고 사라지면 섹션을 옮길 때마다 화면
        // 배치가 바뀌고, 사용자는 상세의 경계를 매번 다시 찾아야 한다.
        <div className={styles.configureBody} aria-hidden="true" />
      )}
      {/* 편집본을 버리는 확인. 취소·X·Esc 는 아무것도 하지 않는다. 닫으려던 사용자가 편집을 잃지
          않도록 dismiss 는 부작용이 없어야 한다. */}
      <HatTextModal
        open={pendingDiscard !== null}
        label={
          pendingDiscard === "reset" ? "본문을 원본으로 되돌립니다." : "편집한 본문을 버립니다."
        }
        caption={
          pendingDiscard === "reset"
            ? "직접 편집한 내용은 복구할 수 없습니다. 본문 직접 편집 방식은 그대로 유지됩니다."
            : "overrides 방식으로 돌아갑니다. 직접 편집한 내용은 복구할 수 없고, 전환 전에 있던 overrides 는 그대로 복원됩니다."
        }
        okText={pendingDiscard === "reset" ? "원본으로 되돌리기" : "본문 버리고 돌아가기"}
        error
        onOkPress={confirmDiscard}
        onCancelPress={() => setPendingDiscard(null)}
      />
    </div>
  );
}

// 선택된 섹션 하나의 본문. 제목은 머리글이 소유하므로 여기서 다시 적지 않는다. 한 섹션 안의
// 여러 블록은 테두리가 아니라 간격으로 나눈다(.configureSectionBody 의 gap).
function ConfigureSectionBody({
  section,
  controller,
  configEdit,
}: StepProps & { section: ConfigureSection; configEdit: ConfigEditProps }): ReactNode {
  const { form, setDatasets, updateForm, annotationSets, annotationMaskNotice } = controller;
  switch (section) {
    case "experiment":
      return <ExperimentControls controller={controller} />;
    case "datasets":
      // 시작 위치(학습 방식) → 학습 데이터(데이터셋) 순서로 한 화면에 둔다(사용자 확정).
      // 시작점이 데이터셋 구성을 좁히지는 않는다. 단일이든 여럿이든 scratch·finetune·retrain 을
      // 똑같이 고를 수 있다. 다만 정하는 순서가 이 순서다.
      return (
        <>
          <SubmitField label="Training 방식">
            <InitStrategyPanel controller={controller} />
          </SubmitField>
          <SubmitField label="Dataset" dividerTop hint={annotationMaskNotice ?? undefined}>
            <SelectedDatasetList
              datasets={form.datasets}
              targetDatasetIndex={form.targetDatasetIndex}
              annotationSets={annotationSets}
              maskGenerationBlocked={controller.maskGenerationBlocked}
              onChange={setDatasets}
              onTargetChange={(index) => updateForm({ targetDatasetIndex: index })}
            />
            <AnnotationSetDropNotice controller={controller} />
          </SubmitField>
        </>
      );
    case "config":
      // 레시피 출처(브랜치·config 파일)와 config 수정 방법(적용 방식. override 목록 또는 본문 직접
      // 편집)이 한 묶음이다. 그 아래는 config 위에 추가되는 노브(mux·해상도·옵션)다. 어느 값을
      // 바꿔도 옆의 config 본문에 결과가 표시된다. 적용 방식을 mux 아래에 두면 긴 채널 토글 그룹
      // 때문에 스크롤하기 전에는 보이지 않는다.
      return (
        <>
          <RecipeSourceControls controller={controller} />
          <ConfigApplyModeField controller={controller} configEdit={configEdit} />
          {/* 출처·적용 방식 묶음과 노브 묶음을 구분선으로 나눈다. 두 묶음의 성격이 다르기 때문이다.
              하나는 config 본문을 정하고, 다른 하나는 그 위에 자동 override 로 추가된다. */}
          <SubmitField label="mux" dividerTop>
            {controller.sigmaDefaultsNotice ? (
              <p className={styles.hint} role="status">
                {controller.sigmaDefaultsNotice}
              </p>
            ) : null}
            <ChannelSelectionEditor controller={controller} />
          </SubmitField>
          {/* 카메라 선택 바로 다음에, 그 이미지가 인코더에 들어가는 크기를 둔다. */}
          <ImageSizeControl controller={controller} />
          <OptionControls controller={controller} />
        </>
      );
    case "evaluation":
      // 시뮬레이션 데이터셋이 있을 때만 표시하는 섹션. 학습이 끝난 체크포인트로 Isaac 평가를 이어서
      // 실행할지와 그 파라미터를 정한다. 보낼 값은 오른쪽 열의 확인 요약이 표시한다.
      return <EvaluationControls controller={controller} />;
    case "resources":
      return <ResourceControls controller={controller} />;
  }
}

// Isaac Evaluation 섹션 상세. 켜기 토글과 공용 평가 파라미터 패널로 구성한다.
function EvaluationControls({ controller }: StepProps): ReactNode {
  const {
    isaacEvaluationEnabled,
    isaacEvaluationParams,
    isaacEvaluationPlan,
    setIsaacEvaluationEnabled,
    submitting,
  } = controller;
  return (
    <>
      <div className={styles.evaluationToggle}>
        <div className={styles.checkRowText}>
          <Text variant={hatcheryTextModes["caption-bold"]}>Isaac Evaluation</Text>
          <p className={styles.hint}>
            After training completes, run Isaac Evaluation with the final checkpoint.
          </p>
        </div>
        <Switch
          id="training-submit-isaac-evaluation"
          checked={isaacEvaluationEnabled}
          disabled={submitting}
          aria-label="Use Isaac Evaluation"
          onChange={(event) => setIsaacEvaluationEnabled(event.target.checked)}
        />
      </div>
      {isaacEvaluationEnabled ? (
        <>
          <EvaluationParametersPanel params={isaacEvaluationParams} disabled={submitting} />
          {isaacEvaluationPlan.errorMessage ? (
            <p className={styles.errorText}>{isaacEvaluationPlan.errorMessage}</p>
          ) : null}
        </>
      ) : (
        <EmptyState size="sm">
          Training will run without evaluation. Turn on the toggle to include evaluation.
        </EmptyState>
      )}
    </>
  );
}

// 평가가 보낼 값. 평가 시작 확인 다이얼로그와 같은 요약이다(이미지·실행 횟수·파라미터
// Default/Current). 구성 스텝 오른쪽 열과 확정 스텝 diff 아래가 같은 블록을 쓴다. 정책은 없다.
// 아직 학습되지 않은 세션에 붙는 평가이기 때문이다.
function IsaacEvaluationSummaryBlock({
  title,
  controller,
}: StepProps & { title: string }): ReactNode {
  const { isaacEvaluationEnabled, isaacEvaluationParams, isaacEvaluationPlan } = controller;
  const paramsLoaded =
    isaacEvaluationParams.status === "success" && isaacEvaluationParams.specsStatus === "success";
  return (
    <div className={styles.configBodyBlock}>
      <div className={styles.configBodyHead}>
        <Text variant={hatcheryTextModes["caption-bold"]}>{title}</Text>
      </div>
      {!isaacEvaluationEnabled ? (
        <EmptyState size="sm">
          Isaac Evaluation을 사용하지 않습니다. Training만 실행합니다.
        </EmptyState>
      ) : paramsLoaded ? (
        <EvalStartSummary
          selectedTag={isaacEvaluationParams.selectedTag}
          imageUri={isaacEvaluationParams.imageUri}
          specs={isaacEvaluationParams.specs}
          values={isaacEvaluationParams.values}
          seedCount={isaacEvaluationPlan.seedPlan?.seeds.length ?? null}
        />
      ) : (
        <LoadingState size="sm">Evaluation 파라미터를 불러오고 있습니다.</LoadingState>
      )}
    </div>
  );
}

// config 출처. Branch → Commit → config 파일(이름 = repo path) 순서다. 본문은 오른쪽 열이 표시한다.
//
// 목록 로딩은 드롭다운 **안**에 표시한다. 트리거를 잠그고 그 문구를 로딩 문구로 바꾼다. c6 드롭다운에는
// loading 상태가 없고 `placeholder` 는 value 가 null 일 때만 쓰인다. 그래서 값이 `""` 인 센티널 옵션의
// 라벨이 트리거 문구가 된다. 밖에 한 줄을 더 두면 라벨·컨트롤·안내의 세 층이 되어, 사용자가 안내가
// 어느 컨트롤의 것인지 한 번 더 찾아야 한다. 목록이 비어 있는 동안이라 잠가도 잃는 것이 없다.
//
// Commit 은 Commit 고정을 여는 위저드에서만 표시한다(`features.commitPin`). Schedule 은 Branch 의
// 최신 코드를 반복해 학습하는 축이라 고정할 커밋이 없다.
function RecipeSourceControls({ controller }: StepProps): ReactNode {
  const {
    optionsState,
    form,
    features,
    branchConfigsState,
    configBodyLoading,
    selectBranch,
    selectConfig,
  } = controller;
  const branches = optionsState.status === "success" ? optionsState.data.branches : [];
  const branchesLoading = optionsState.status === "idle" || optionsState.status === "loading";
  // submit-options fan-out 에서 브랜치 섹션이 실패하면(learning-core 연결 등) 빈 목록이 온다.
  const branchesUnavailable =
    optionsState.status === "success" && optionsState.data.unavailable.includes("branches");
  const branchOptions = [
    { value: "", label: branchesLoading ? "Branch 목록을 불러오는 중…" : "Branch 선택" },
    ...branches.map((branch) => ({ value: branch.name, label: branch.name })),
  ];
  // 프리필이 가져온 브랜치가 그 사이 없어졌을 수 있다. c6 드롭다운은 목록에 없는 값도 그대로
  // 트리거에 그리므로 여기서 알린다. 고정한 커밋이 있으면 config 를 sha 로 받으므로 제출할 수 있다.
  const branchMissing =
    form.branchRef !== "" &&
    optionsState.status === "success" &&
    branches.length > 0 &&
    !branches.some((branch) => branch.name === form.branchRef);
  const configs = branchConfigsState.status === "success" ? branchConfigsState.data.configs : [];
  const configsLoading = branchConfigsState.status === "loading";
  const configOptions = [
    {
      value: "",
      label:
        learningCoreRef(form) === ""
          ? "Branch 먼저 선택"
          : configsLoading
            ? "config 목록을 불러오는 중…"
            : "config 선택",
    },
    ...configs.map((config) => ({ value: config.path, label: config.path })),
  ];
  return (
    <>
      <SubmitField label="Branch" required>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="Branch"
          className={styles.fullDropdown}
          value={form.branchRef}
          options={branchOptions}
          onValueChange={selectBranch}
          disabled={branchesLoading}
          searchable
          searchPlaceholder="Branch 를 검색해 보세요."
        />
        {branchesUnavailable ? (
          <p className={styles.errorText}>
            Branch 목록을 불러오지 못했습니다. learning-core 연결 상태를 확인해 주세요.
          </p>
        ) : null}
        {branchMissing ? (
          <p className={styles.errorText}>
            {form.commitPin === null
              ? "이 Branch 는 목록에 없습니다. 삭제됐거나 이름이 바뀌었을 수 있으니 다른 Branch 를 골라 주세요."
              : "이 Branch 는 목록에 없습니다. 삭제됐거나 이름이 바뀌었을 수 있지만, 고정한 Commit 으로는 제출할 수 있습니다."}
          </p>
        ) : null}
      </SubmitField>
      {features.commitPin ? <CommitPinField controller={controller} /> : null}
      <SubmitField label="config 파일" required>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="config 파일"
          className={styles.fullDropdown}
          value={form.config.configName}
          options={configOptions}
          onValueChange={selectConfig}
          disabled={configsLoading}
          searchable
          searchPlaceholder="config 파일을 검색해 보세요."
        />
        {branchConfigsState.status === "error" ? (
          <p className={styles.errorText}>{branchConfigsState.message}</p>
        ) : null}
        {/* 본문 로딩은 목록이 아니라 고른 파일의 내용이라, 드롭다운을 열어 둔 채 밖에 표시한다.
            목록 로딩은 트리거 문구가 이미 표시하므로 여기는 본문 단계만 표시한다. */}
        {configBodyLoading ? (
          <LoadingState size="sm">본문을 불러오고 있습니다.</LoadingState>
        ) : null}
      </SubmitField>
    </>
  );
}

// Commit 은 Branch 위에 추가하는 고정이다. 센티널 `""` 은 "Branch 최신"(= `commitPin: null`)이고, 커밋을
// 고르면 그 SHA 가 제출 ref 가 된다. 목록 밖 커밋이 고정되어 있으면(프리필·구간 밖·목록 실패) 합성
// 옵션을 만든다. 오래된 Policy 의 커밋은 최근 목록에 없어서 값이 빈 것처럼 보이기 때문이다.
function CommitPinField({ controller }: StepProps): ReactNode {
  const {
    form,
    commitsState,
    branchTipSha,
    finetuneRevisionSuggestion,
    selectCommit,
    applyLearningCoreRevision,
  } = controller;
  const commitsLoading = commitsState.status === "loading";
  // 최대 200개 옵션을 렌더마다 다시 만들지 않는다. 폼의 다른 입력이 바뀌어도 목록은 같다.
  const branchRef = form.branchRef;
  const commitPin = form.commitPin;
  const commitOptions = useMemo(
    () => buildCommitOptions({ branchRef, commitPin }, commitsState, branchTipSha),
    [branchRef, commitPin, commitsState, branchTipSha],
  );
  const commitHint = commitHintFor(form, commitsState, finetuneRevisionSuggestion !== null);
  return (
    <SubmitField label="Commit" hint={commitHint}>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="Commit"
        className={styles.fullDropdown}
        value={form.commitPin ?? BRANCH_TIP_VALUE}
        options={commitOptions}
        onValueChange={(value) => selectCommit(value === BRANCH_TIP_VALUE ? null : value)}
        disabled={commitsLoading || learningCoreRef(form) === ""}
        // 메뉴는 트리거 폭을 따른다. 기본 22rem 상한을 쓰면 필드보다 좁은 창에서 긴 subject 가 잘린다.
        // 잘린 라벨·설명은 hover 때 c6 잘림 툴팁이 전체 값을 표시한다(옵션이 마커를 가진다).
        menuWidth="matchTrigger"
        searchable
        searchPlaceholder="Commit 을 검색해 보세요."
      />
      {/* 목록 실패는 제출을 막지 않는다. Branch 최신은 목록 없이도 고를 수 있다. */}
      {commitsState.status === "error" ? (
        <p className={styles.errorText}>
          {form.commitPin === null
            ? "Commit 목록을 불러오지 못했습니다. Branch 최신으로 제출할 수 있습니다."
            : "Commit 목록을 불러오지 못했습니다. 고정한 Commit 그대로 제출할 수 있습니다."}
        </p>
      ) : null}
      {/* fine-tune 제안. 자동 적용하지 않는다. 누르면 브랜치·커밋을 함께 그 시점으로 설정한다. */}
      {finetuneRevisionSuggestion !== null ? (
        <div className={styles.revisionNotice} role="status">
          <span>
            선택한 Policy 는{" "}
            <BranchHashCell
              branch={finetuneRevisionSuggestion.ref}
              hash={shortSha(finetuneRevisionSuggestion.sha)}
              fullHash={finetuneRevisionSuggestion.sha}
            />{" "}
            에서 학습되었습니다.
          </span>
          <IconButton
            text="이 Commit 으로 맞추기"
            size="sm"
            variant="surface"
            onClick={() =>
              applyLearningCoreRevision(
                finetuneRevisionSuggestion.ref,
                finetuneRevisionSuggestion.sha,
              )
            }
          />
        </div>
      ) : null}
    </SubmitField>
  );
}

// c6 드롭다운은 값이 문자열이라 "Branch 최신"(= `commitPin: null`)에 빈 값을 센티널로 쓴다.
// 폼과 컨트롤 사이의 변환은 이 상수를 쓰는 두 줄(value·onValueChange)과 옵션 빌더뿐이다.
const BRANCH_TIP_VALUE = "";

// Commit 드롭다운의 옵션 목록. 센티널(Branch 최신) → 목록 밖 고정 커밋(합성) → 최근 커밋 순서다.
function buildCommitOptions(
  form: Pick<SubmitFormState, "branchRef" | "commitPin">,
  commitsState: SubmitWizardController["commitsState"],
  branchTipSha: string | null,
): SingleSelectDropdownOption[] {
  const listed = commitsState.status === "success";
  const commits = listed ? commitsState.data.commits : [];
  const pin = form.commitPin;
  // "목록 밖"은 목록이 있을 때만 뜻이 있다. 로딩·실패·목록 없음(브랜치 없는 고정)에서는 해시만 적어서
  // 정상 커밋이 모르는 값처럼 보이지 않게 한다.
  const pinnedOutsideList =
    pin !== null && !commits.some((commit) => commit.sha === pin)
      ? [{ value: pin, label: listed ? `${shortSha(pin)} · (목록 밖 Commit)` : `${shortSha(pin)}` }]
      : [];
  if (form.branchRef === "") {
    // 브랜치가 없으면 "최신"이 가리킬 대상이 없다. 고정 커밋만 표시하거나 브랜치를 먼저 고르게 한다.
    return pin === null
      ? [{ value: BRANCH_TIP_VALUE, label: "Branch 먼저 선택" }]
      : pinnedOutsideList;
  }
  const tip = shortSha(branchTipSha);
  const sentinel = {
    value: BRANCH_TIP_VALUE,
    label:
      commitsState.status === "loading"
        ? "Commit 목록을 불러오는 중…"
        : tip === null
          ? "Branch 최신"
          : `Branch 최신 (${tip})`,
  };
  return [sentinel, ...pinnedOutsideList, ...commits.map(commitOption)];
}

function commitOption(commit: BranchCommitView): SingleSelectDropdownOption {
  return {
    value: commit.sha,
    label: `${commit.short_sha} · ${commit.subject}`,
    description: `${commit.author} · ${formatDateTime(parseUtcMs(commit.committed_at))}`,
  };
}

// Commit 필드 아래 안내. 브랜치 없이 커밋만 고정된 상태(40-hex 제출의 재제출)는 목록이 없어서
// 고를 항목이 그 하나뿐이다. 제안 커밋이 구간 창 밖이면 제안으로 연결한다.
function commitHintFor(
  form: Pick<SubmitFormState, "branchRef" | "commitPin">,
  commitsState: SubmitWizardController["commitsState"],
  hasSuggestion: boolean,
): string | undefined {
  if (form.branchRef === "" && form.commitPin !== null) {
    return "Branch 를 고르면 다른 Commit 을 고를 수 있습니다.";
  }
  if (
    hasSuggestion &&
    commitsState.status === "success" &&
    commitsState.data.since_reached === false
  ) {
    return `선택한 Policy 가 학습된 Commit 은 최근 ${BRANCH_COMMITS_SINCE_LIMIT}개 안에 없습니다. 아래 제안으로 그 시점에 맞출 수 있습니다.`;
  }
  return undefined;
}

// config override 입력. 자유 키/값이라 행 하나에 고정 폭이 필요하다. 그래서 이 섹션이 상세 열의
// 폭을 모두 쓴다.
function OverrideControls({ controller }: StepProps): ReactNode {
  const { form, configFamily, configData, updateConfig } = controller;
  return (
    <RecipeOverrideEditor
      pairs={form.config.overrides}
      onChange={(overrides) => updateConfig({ overrides })}
      family={configFamily}
      configData={configData}
      ariaLabel="config override"
    />
  );
}

// 적용 방식 스위처는 내용 폭이다. 열을 채우면 세그먼트를 균등 분할해서 긴 라벨이 자기 칸을 넘는다
// (InitStrategyPanel 의 출처 스위처와 같은 이유).
const CONFIG_APPLY_MODE_WIDTH = { type: "intrinsic" } as const;

// config 적용 방식. overrides 와 본문 직접 편집 중 하나다. config 파일 바로 아래에 두고, 스위처
// 아래에 그 방식의 입력(override 목록 또는 YAML 에디터)을 둔다. 진입은 행을 본문에 반영하므로 확인이
// 없다. 되돌아오기는 편집본을 버리므로 스텝이 확인을 요청한다(onModeChange). 원본을 읽을 수 없으면
// 반영할 수 없으므로 세그먼트를 잠근다.
function ConfigApplyModeField({
  controller,
  configEdit,
}: StepProps & { configEdit: ConfigEditProps }): ReactNode {
  const { form, configEditDiscarded, directEditBlockedReason } = controller;
  const direct = isDirectConfigEdit(form.config);
  const options: ContentSwitcherOption[] = [
    {
      value: "overrides",
      label: "overrides",
      tooltip: "dot-notation 키로 값을 덮어씁니다. 제출은 repo config 이름과 override 로 나갑니다.",
    },
    {
      value: "direct",
      label: "본문 직접 편집",
      tooltip: "config 본문 전문을 직접 고쳐 제출합니다. 지금의 override 는 본문에 합쳐집니다.",
      disabled: directEditBlockedReason !== null,
      disabledTooltip: directEditBlockedReason ?? undefined,
    },
  ];
  return (
    <SubmitField label="config 적용 방식">
      <div className={styles.configApplyModeSwitcher}>
        <ContentSwitcher
          label="config 적용 방식"
          size="md"
          width={CONFIG_APPLY_MODE_WIDTH}
          options={options}
          value={direct ? "direct" : "overrides"}
          onValueChange={(value) =>
            configEdit.onModeChange(value === "direct" ? "direct" : "overrides")
          }
        />
      </div>
      {/* config 를 바꾸면 직접 편집이 overrides 방식으로 바뀐다. 이 사실은 항상 이 자리(overrides 쪽)에 표시한다. */}
      {configEditDiscarded ? (
        <p className={styles.hint}>
          config 를 바꾸면서 직접 편집한 본문을 버리고 overrides 방식으로 돌아왔습니다.
        </p>
      ) : null}
      {direct ? (
        <ConfigDirectEditor controller={controller} configEdit={configEdit} />
      ) : (
        <OverrideControls controller={controller} />
      )}
    </SubmitField>
  );
}

// 본문 직접 편집의 에디터. 섹션 본문 안에 인라인으로 표시한다. 타이핑은 초안에만 쌓이고 blur 때 폼에
// 반영한다. 입력 본문에 관한 안내(제출 내용·크기·파싱 오류)는 모두 여기 있고, 옆 열은 결과만 표시한다.
function ConfigDirectEditor({
  controller,
  configEdit,
}: StepProps & { configEdit: ConfigEditProps }): ReactNode {
  const { form, configBodyLoading, configParseErrors } = controller;
  const editError = configEditError(form.config);
  return (
    <>
      <div className={styles.configBodyToolbar}>
        <p className={styles.hint}>
          편집한 본문 전문으로 제출합니다. RandomBackground·입력 해상도·Checkpoint 옵션은 이 본문
          위에 적용됩니다.
        </p>
        <IconButton
          text="원본으로 되돌리기"
          variant="surface"
          size="sm"
          disabled={!configEdit.dirty}
          aria-label="config 본문을 원본으로 되돌리기"
          onClick={configEdit.onResetRequest}
        />
      </div>
      {configBodyLoading ? (
        <div
          className={`${styles.configEditorShell} ${styles.configEditorInline} ${stateFrameClassName}`}
        >
          <LoadingState mode="plain">본문을 불러오고 있습니다.</LoadingState>
        </div>
      ) : (
        <div className={`${styles.configEditorShell} ${styles.configEditorInline}`}>
          <CodeEditor
            language="yaml"
            value={configEdit.draft ?? activeConfigYaml(form.config)}
            onChange={configEdit.onDraftChange}
            onBlur={configEdit.onDraftBlur}
            ariaLabel="config 본문 편집"
          />
        </div>
      )}
      {editError !== null ? <p className={styles.errorText}>{editError}</p> : null}
      {configParseErrors.length > 0 ? (
        <p className={styles.errorText}>YAML 을 읽지 못했습니다: {configParseErrors.join(" · ")}</p>
      ) : null}
    </>
  );
}

// config 의 boolean 노브 모음. 지금은 random background 하나다. 자주 쓰는 나머지 옵션도
// 같은 행 형태로 여기에 추가한다.
function OptionControls({ controller }: StepProps): ReactNode {
  const { form, configData, setRandomBackground } = controller;
  const available = configData !== null && hasRandomBackgroundTransform(configData);
  // 켜진 기본 상태는 설명하지 않는다. "원본대로"는 아무것도 바꾸지 않는다는 뜻이라 자리만 차지한다.
  const description = !available
    ? "config 본문에서 RandomBackground 를 찾지 못했습니다."
    : form.config.randomBackgroundEnabled
      ? undefined
      : "제출 시 RandomBackground 항목을 제거합니다.";
  return (
    <>
      <ToggleField
        label="RandomBackground 활성화"
        hint={description}
        checked={form.config.randomBackgroundEnabled}
        disabled={!available}
        onChange={setRandomBackground}
      />
      <SaveOnlyLastCheckpointField controller={controller} />
    </>
  );
}

// 체크포인트를 마지막 하나만 남기는 노브. learning-core 에 그런 스위치가 없어서 두 값을 함께
// 설정한다. top-k 저장을 끄고, 주기 저장이 마지막 unit 에서만 일어나도록 주기를 학습 길이만큼 늘린다.
// 주기 값은 예산에서 계산하므로 학습 길이를 바꾸면 함께 바뀐다(폼에 고정하지 않는다).
function SaveOnlyLastCheckpointField({ controller }: StepProps): ReactNode {
  const { form, budgetForm, saveOnlyLastCheckpointSaveEvery, setSaveOnlyLastCheckpoint } =
    controller;
  const enabled = form.config.saveOnlyLastCheckpoint;
  // 길이를 읽을 수 없으면 주기를 계산할 수 없어서 켜도 효과가 없다. 켜지 못하게 막고 이유를 표시한다.
  const lengthKnown = saveOnlyLastCheckpointSaveEvery !== null;
  // 꺼진 기본 상태는 설명하지 않는다. "config 대로"는 아무것도 바꾸지 않는다는 뜻이라 자리만 차지한다.
  const hint = !budgetForm
    ? "이 레시피에는 train.budget 이 없어 저장 주기를 정할 수 없습니다."
    : enabled && !lengthKnown
      ? "Training 길이를 먼저 정해 주세요. 저장 주기를 그 값에서 계산합니다."
      : enabled
        ? `마지막 Checkpoint 하나만 남깁니다(저장 주기 ${saveOnlyLastCheckpointSaveEvery}).`
        : undefined;
  return (
    <ToggleField
      label="마지막 Checkpoint만 저장"
      hint={hint}
      checked={enabled}
      disabled={!budgetForm}
      onChange={setSaveOnlyLastCheckpoint}
    />
  );
}

// 비전 인코더 입력 해상도 (H, W). 폼이 값을 바꾸지 않았으면 config 현재 값(망 기본값)을 실제 값으로
// 자동 입력해 표시한다. 표시 소스가 config 라서 본문 편집도 그대로 반영된다. override 는 폼 값이
// 제출될 본문의 값과 다를 때만 보낸다(submitWizardForm.automaticConfigOverrides).
//
// 카메라 shape와 인코더 입력 크기는 별개다. sigma_hand_1.2의 238×294 기본값은
// 컨트롤러가 정한다. 이 필드는 폼 값이 없을 때만 config 값으로 표시한다.
function ImageSizeControl({ controller }: StepProps): ReactNode {
  const { form, configImageSize, setImageSize } = controller;
  const current = form.config.imageSize ?? configImageSize;

  const commit = (patch: { height?: number; width?: number }) => {
    const height = patch.height ?? current?.height ?? 0;
    const width = patch.width ?? current?.width ?? 0;
    setImageSize(height > 0 && width > 0 ? { height, width } : null);
  };

  return (
    <SubmitField
      label="입력 해상도 (H × W)"
      hint="비전 인코더 입력 크기입니다. config 또는 Dataset 기본값이 자동 입력되며, 직접 수정할 수 있습니다."
    >
      <div className={styles.fieldRow}>
        <TextInput
          value={current === null ? "" : String(current.height)}
          placeholder="H"
          aria-label="입력 해상도 높이"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            commit({ height: clampPositiveInt(event.target.value, 0) })
          }
        />
        <TextInput
          value={current === null ? "" : String(current.width)}
          placeholder="W"
          aria-label="입력 해상도 너비"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            commit({ width: clampPositiveInt(event.target.value, 0) })
          }
        />
      </div>
    </SubmitField>
  );
}

// 리소스 컨트롤. nodes/gpus(기본값 시드), LR 자동 계산, extra 패스스루로 구성한다.
function ResourceControls({ controller }: StepProps): ReactNode {
  const { form, configData, updateForm } = controller;
  const batchRow = form.config.overrides.find(
    (pair) => parseOverrideKey(pair.key).path === BATCH_SIZE_OVERRIDE_KEY,
  );
  const autoLrPreview =
    configData === null
      ? null
      : computeAutoLr(configData, form.nodes * form.gpusPerNode, batchRow?.value ?? null);
  // 직접 편집 중에는 lr 행을 쓸 override 목록이 없다. 본문에서 직접 정하도록 잠근다.
  const directEdit = isDirectConfigEdit(form.config);
  const autoLrDescription = directEdit
    ? "본문 직접 편집 중에는 lr 을 본문에서 직접 정합니다."
    : form.autoLr
      ? autoLrPreview === null
        ? "config의 lr·batch_size를 읽지 못해 계산할 수 없습니다."
        : `config lr ${autoLrPreview.baseLr} × ${formatFactor(autoLrPreview.factor)}배 (global batch ${autoLrPreview.globalBatch}) → ${autoLrPreview.lr}`
      : "켜면 GPU 수와 배치에 비례해 lr override를 자동으로 갱신합니다.";
  // finetune 은 learning-core 진입이 단일 프로세스라 1×1 이 아니면 BE 가 400 을 준다. 값을 잠가서
  // 위반이 구조적으로 생기지 않게 한다.
  const resourcesLocked = form.init.strategy === "finetune";
  // 전용 노브가 소유하는 키는 목록에서 숨긴다. 같은 값의 입력 위치가 둘이 되지 않게 한다.
  const isManagedRow = (pair: KeyValuePair) => isManagedExtraKey(pair.key);
  const visibleExtra = form.resourceExtra.filter((pair) => !isManagedRow(pair));
  return (
    <>
      <SubmitField
        label="nodes · gpus_per_node"
        hint={
          resourcesLocked
            ? "finetune 은 단일 프로세스로 돌아 1 node × 1 GPU로 고정됩니다."
            : undefined
        }
      >
        <div className={styles.fieldRow}>
          <TextInput
            value={String(form.nodes)}
            aria-label="nodes"
            disabled={resourcesLocked}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateForm({ nodes: clampPositiveInt(event.target.value, form.nodes) })
            }
          />
          <TextInput
            value={String(form.gpusPerNode)}
            aria-label="gpus_per_node"
            disabled={resourcesLocked}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateForm({ gpusPerNode: clampPositiveInt(event.target.value, form.gpusPerNode) })
            }
          />
        </div>
      </SubmitField>
      <ToggleField
        label="auto learning rate (선형 스케일)"
        hint={autoLrDescription}
        checked={form.autoLr}
        disabled={directEdit}
        onChange={(autoLr) => updateForm({ autoLr })}
      />
      {/* 스팟은 켬/끔이 아니라 3-상태다. "클러스터 기본"은 제출에서 키를 빼는 상태라 스위치로는
          표현할 수 없다. 상시 힌트는 고른 값만 설명하므로 나머지는 옵션 툴팁으로 미리 표시한다. */}
      <SubmitField label="스팟 노드 허용" hint={allowSpotChoiceHint(form.allowSpot)}>
        <ContentSwitcher
          size="sm"
          label="스팟 노드 허용"
          options={allowSpotOptions}
          value={allowSpotChoiceValue(form.allowSpot)}
          onValueChange={(value) => updateForm({ allowSpot: allowSpotFromChoiceValue(value) })}
        />
      </SubmitField>
      {/* finetune 제출에서는 자리를 비운다. 상류는 fine-tune entry 로 진입해 이 플래그를 읽지 않는다.
          거절도 경고도 없어서, 켜 두면 사용자는 켜졌다고 여긴다. */}
      {form.init.strategy === "scratch" ? (
        <SwitchField
          label="재시도 시 이어서 학습"
          checked={form.resumeOnRetry}
          onHint="자동 재시도가 직전 시도의 Checkpoint에서 이어 갑니다. 데이터 순서는 재현되지만 dropout·augmentation 의 난수 상태는 Checkpoint에 없어, 끊기지 않고 돈 Training과 완전히 같지는 않습니다."
          offHint="자동 재시도가 처음부터 다시 돕니다. 스팟 회수나 노드 수명 만료로 끊기면 돌린 epoch 을 버립니다."
          onChange={(resumeOnRetry) => updateForm({ resumeOnRetry })}
        />
      ) : null}
      <SubmitField
        label="노드 잔여 수명 하한"
        hint={
          // 명시 거부일 때만 회수 문구를 뺀다. "클러스터 기본"은 스팟이 켜질 수 있는 상태라
          // 회수 위험 안내가 계속 유효하다.
          form.allowSpot === false
            ? "이 시간보다 오래 남은 노드에만 배치합니다."
            : "이 시간보다 오래 남은 노드에만 배치합니다. 스팟을 켰을 때 회수 위험을 여기서 줄입니다."
        }
      >
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="노드 잔여 수명 하한"
          className={styles.fullDropdown}
          value={String(form.minLifetimeSeconds)}
          options={MIN_LIFETIME_OPTIONS.map((option) => ({
            value: String(option.seconds),
            label: option.label,
          }))}
          onValueChange={(value) => updateForm({ minLifetimeSeconds: Number(value) })}
        />
      </SubmitField>
      <SubmitField
        label="extra"
        hint="node_pool 등 training 패스스루 노브입니다(검증은 training 이 소유합니다)."
      >
        <KeyValueEditor
          pairs={visibleExtra}
          onChange={(rows) =>
            updateForm({ resourceExtra: [...rows, ...form.resourceExtra.filter(isManagedRow)] })
          }
          keyPlaceholder="리소스 옵션 키를 입력해 주세요(예: node_pool)."
          valuePlaceholder="리소스 옵션 값을 입력해 주세요."
          ariaLabel="resource extra"
        />
      </SubmitField>
    </>
  );
}

// 초안 타이핑을 잠시 늦춘 값. 실시간 diff 가 글자마다 YAML 왕복을 실행하지 않게 한다.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// 구성 스텝 오른쪽 열. 원본 대비 변경 diff 를 항상 표시한다(읽기 전용). 어느 노브를 바꿔도(override·
// 해상도·RandomBackground) 결과가 즉시 여기 표시된다. 본문 직접 편집 중에는 초안(디바운스)을 제출될
// 본문으로 보고 diff 를 실시간으로 갱신한다. 편집과 편집 안내(오류·제출 내용)는 섹션 본문의
// 에디터가 소유한다. 여기는 결과만 표시한다.
//
// diff 기준은 레포 원본 vs "제출될 본문"(편집본 + 자동 노브 override 적용)이다. mux 주입까지 반영한
// 최종 diff 는 확정 스텝이 BE preview 로 그린다.
function ConfigDiffPane({
  controller,
  draft,
}: StepProps & {
  // 직접 편집의 커밋하지 않은 초안(없으면 null).
  draft: string | null;
}): ReactNode {
  const { form, recipeReloading } = controller;
  const debouncedDraft = useDebouncedValue(draft, 300);
  const renderModel = useMemo(() => {
    // 초안이 있으면 초안이 제출될 본문이다. 커밋 전이라도 diff 가 초안을 따라야
    // "고치는 중인 결과"를 표시한다. 반쯤 입력한 YAML 은 applyOverridesToYaml 이 본문을
    // 그대로 반환하므로 diff 가 깨지지 않는다.
    const config =
      debouncedDraft === null ? form.config : { ...form.config, editedYaml: debouncedDraft };
    return buildConfigRenderModel(config);
  }, [form.config, debouncedDraft]);
  return (
    <div className={styles.configureBody}>
      <div className={`${styles.configBodyBlock} ${styles.configBodyFill}`}>
        <ConfigDiffView
          model={renderModel}
          loading={recipeReloading}
          configSelected={form.config.configName !== ""}
        />
        {/* 명시적 카메라 override는 여기 반영하지만, 채널 선택에 따른 전체 mux는 BE가 재조립한다. */}
        {form.channelSelection !== null ? (
          <p className={styles.hint}>
            카메라 기본값과 직접 수정한 값은 위 변경 사항에 표시합니다. 채널 선택까지 반영한 최종
            mux는 확정 단계 미리보기에서 확인하세요.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ③ 확정. 왼쪽은 제출 요약·config 경고, 오른쪽은 원본 대비 config diff 와 (시뮬레이션 데이터셋일
// 때만) Isaac Evaluation 이 보낼 내용이다. 평가는 왼쪽 요약 행이 아니라 오른쪽 블록이 표시한다.
// 이미지·파라미터 표는 한 줄 요약으로 줄일 수 없다.
export function ConfirmStep({ controller }: StepProps): ReactNode {
  const {
    form,
    features,
    branchTipSha,
    confirmPreviewState,
    confirmValidationState,
    submitError,
    evalWarnings,
    isaacEvaluationAvailable,
    annotationSets,
    annotationMaskNotice,
    recipeReloading,
  } = controller;
  // 코드 출처는 Policy·Results 표와 같은 `Branch@Hash` 와 고정 여부다(상태 표시라 체언 종결).
  // Commit 을 고정할 수 없는 위저드는 Branch 이름만 표시한다. 실행 시점마다 tip 이 다르므로
  // 지금의 해시를 적으면 실행될 코드를 잘못 알린다.
  const codeSourceSummary = (
    <>
      <BranchHashCell {...codeSourceParts(form, features.commitPin ? branchTipSha : null)} />
      {learningCoreRef(form) !== "" && features.commitPin
        ? ` (${form.commitPin === null ? "Branch 최신" : "Commit 고정"})`
        : null}
    </>
  );
  // BE preview(채널 mux 주입 포함)가 있으면 그 overrides 로 diff 를 계산한다. 그래서 화면의 diff 와
  // 실제 제출물이 항상 같다. 실패하면 로컬 계산으로 폴백한다(제출은 가능).
  const preview = confirmPreviewState.status === "success" ? confirmPreviewState.data : null;
  const renderModel = useMemo(
    () => buildConfigRenderModel(form.config, preview?.overrides),
    [form.config, preview],
  );
  const overrideCount = form.config.overrides.filter((pair) => pair.key.trim() !== "").length;
  const channelSummary =
    form.channelSelection === null ? null : formatChannelSelectionSummary(form.channelSelection);

  // 요약의 target 표기는 제출될 값을 그대로 표시한다. 폼 값을 직접 읽으면 train 축이 줄어서
  // 게이트가 닫힌 뒤에도 화면만 target 을 표시한다(payload 는 생략한다).
  const submittedTargetIndex = buildTargetDatasetIndex(form);

  // 전용 노브가 소유하는 키는 자기 이름의 행으로 따로 요약한다. extra 는 남은 패스스루 값이다.
  const extraSummary = form.resourceExtra
    .filter((pair) => pair.key.trim() !== "" && !isManagedExtraKey(pair.key))
    .map((pair) => `${pair.key}=${pair.value}`)
    .join(" · ");
  return (
    <div className={styles.confirmStep}>
      <div className={styles.confirmControls}>
        <dl className={styles.summary} aria-label="제출 요약">
          {/* 스케줄 위저드는 반복 시점부터 요약한다. 아래 제출 전체 요약과 같은 표에 두어
              확정 화면을 한 번에 읽게 한다. */}
          {hasScheduleStep(controller.features) ? (
            <ScheduleSummaryRows
              draft={controller.scheduleDraft}
              workspaceName={
                controller.workspacesState.status === "success"
                  ? (controller.workspacesState.data.find((item) => item.id === form.workspaceId)
                      ?.name ?? null)
                  : null
              }
            />
          ) : null}
          {/* 라벨은 구성 스텝과 같은 learning-core 이름을 쓴다. 한 마법사 안에서 같은
                필드를 두 어휘로 부르지 않는다. 실험·세션 이름은 registry 축이라 한국어다. */}
          {form.datasets.length === 0 ? (
            <SummaryRow label="datasets" value="—" />
          ) : (
            form.datasets.map((dataset, index) => (
              <SummaryRow
                key={`${dataset.uuid}:${index}`}
                label={`datasets[${index}]`}
                value={formatDatasetSummary(
                  dataset,
                  submittedTargetIndex === index,
                  annotationSetSummary(annotationSets, dataset),
                )}
              />
            ))
          )}
          <SummaryRow label="init" value={formatInitSummary(form.init)} />
          <SummaryRow label="실험" value={form.experimentName ?? (form.experimentId || "—")} />
          {/* 이름은 구성 스텝에서 정한다. 요약은 제출물 전체를 표시하기만 한다. */}
          <SummaryRow label="Session 표시 이름" value={form.displayName.trim() || "서버 지정"} />
          <SummaryRow label="Branch" value={codeSourceSummary} />
          <SummaryRow label="config 파일" value={form.config.configName || "—"} />
          {/* 적용 방식은 배타라 한 줄만 표시한다. 직접 편집이면 override 행이 비어 건수가 뜻이 없다. */}
          {isDirectConfigEdit(form.config) ? (
            <SummaryRow label="config 적용 방식" value="본문 직접 편집" />
          ) : (
            <SummaryRow
              label="overrides"
              value={overrideCount === 0 ? "없음" : `${overrideCount}건`}
            />
          )}
          {channelSummary !== null ? <SummaryRow label="mux" value={channelSummary} /> : null}
          {!form.config.randomBackgroundEnabled ? (
            <SummaryRow label="RandomBackground" value="제출 시 제거" />
          ) : null}
          <SummaryRow
            label="resources"
            value={`${form.nodes} node × ${form.gpusPerNode} GPU (총 ${form.nodes * form.gpusPerNode} GPU)`}
          />
          <SummaryRow label="스팟 노드 허용" value={allowSpotChoiceLabel(form.allowSpot)} />
          <SummaryRow
            label="노드 잔여 수명 하한"
            value={minLifetimeLabel(form.minLifetimeSeconds)}
          />
          {buildResumeOnRetry(form) ? (
            <SummaryRow label="재시도 시 이어서 학습" value="켜짐" />
          ) : null}
          {extraSummary !== "" ? <SummaryRow label="resources.extra" value={extraSummary} /> : null}
          {form.autoLr ? <SummaryRow label="auto learning rate" value="켜짐" /> : null}
          {/* 알림은 이 화면에서 고치지 않는다. Training 헤더 토글이 값을 정하고, 요약은
              제출물에 포함되는 사실만 표시한다. 꺼져 있으면 payload 에 키가 없어서 줄도 없다. */}
          {form.notifyOnFinish ? <SummaryRow label="Training 종료 알림" value="켜짐" /> : null}
        </dl>
        {preview !== null && preview.injected_topics.length > 0 ? (
          <p className={styles.hint}>
            Dataset 채널에 맞게 자동 조정됨: {preview.injected_topics.join(", ")}
          </p>
        ) : null}
        {preview !== null
          ? preview.warnings.map((warning) => (
              <p key={warning} className={styles.hint}>
                {warning}
              </p>
            ))
          : null}
        {confirmPreviewState.status === "error" ? (
          <p className={styles.hint}>
            미리보기를 불러오지 못했습니다. 아래 diff는 자동 조정 전 기준이며 제출할 수 있습니다.
          </p>
        ) : null}
        {/* 상류가 준 eval 설정 안내. 제출을 막지 않으므로 오류가 아니라 hint 로 둔다. */}
        {/* 세트 선택과 mask co-train 스위치가 어긋나면 제출 직전에 한 번 더 표시한다. 사용자가
              구성 스텝을 지난 뒤 config 를 바꿨을 수 있다. 제출은 막지 않는다. */}
        {annotationMaskNotice === null ? null : (
          <p className={styles.hint}>{annotationMaskNotice}</p>
        )}
        {evalWarnings.map((warning) => (
          <p key={warning} className={styles.hint}>
            Evaluation 설정을 확인하세요: {warning}
          </p>
        ))}
        <ValidationReportPanel state={confirmValidationState} />
        {submitError ? <p className={styles.errorText}>{submitError}</p> : null}
      </div>
      <div className={styles.confirmDiffCol}>
        <div className={`${styles.configBodyBlock} ${styles.configBodyFill}`}>
          <ConfigDiffView
            model={renderModel}
            loading={recipeReloading}
            configSelected={form.config.configName !== ""}
          />
        </div>
        {/* 시뮬레이션 데이터셋이 없으면 평가를 선택할 수 없었으므로 블록도 두지 않는다. */}
        {isaacEvaluationAvailable ? (
          <IsaacEvaluationSummaryBlock title="Isaac Evaluation 변경 사항" controller={controller} />
        ) : null}
      </div>
    </div>
  );
}

// config 변경 사항 블록의 머리 줄과 diff. 레포 원본 대비 제출될 본문을 표시한다. 구성 스텝과 확정 스텝이
// 함께 쓴다. 에디터 영역은 고정 높이가 아니라 열의 남은 높이를 받는다.
//
// 브랜치·커밋을 바꾸면 목록 → 본문 조회가 이어지는 동안 본문이 비어 있다(전환이 본문을 동기로
// 비운다). 그때 idle 문구를 표시하면 로딩이 보이지 않고 한참 뒤 diff 가 갑자기 나타난다. 그래서
// 조회 단계 중 하나라도 실행 중이면 스피너를 표시한다.
//
// 대기와 빈 상태는 diff 에디터와 같은 높이의 틀 안에 둔다(docs/rules.md §19). diff 가 오면 같은 자리에
// 그려지므로 열이 움직이지 않는다.
function ConfigDiffView({
  model,
  loading,
  configSelected,
}: {
  model: ReturnType<typeof buildConfigRenderModel>;
  loading: boolean;
  // config 이름을 골랐는지. 골랐는데 본문이 없고 조회도 끝났으면 본문을 받지 못한 것이다.
  configSelected: boolean;
}): ReactNode {
  // 원본 본문이 없으면 diff 를 그리지 않는다. 복사할 것도 없다. override 만으로 만든 조각을 제출될
  // config 처럼 복사하게 두지 않는다. 빈 문자열이면 복사 버튼이 꺼진다.
  const hasBody = model.originalYaml !== "";
  return (
    <>
      <div className={styles.configBodyHead}>
        <Text variant={hatcheryTextModes["caption-bold"]}>config 변경 사항 (원본 대비)</Text>
        <CopyButton text={hasBody ? model.effectiveYaml : ""} />
      </div>
      {!hasBody ? (
        <div className={DIFF_STATE_FRAME}>
          {loading ? (
            <LoadingState mode="plain">config 를 불러오고 있습니다.</LoadingState>
          ) : configSelected ? (
            // 본문 조회 실패는 제출을 막지 않는다. 그래서 오류 톤이 아니다(docs/rules.md §19).
            <EmptyState mode="plain">
              config 본문을 불러오지 못해 변경 사항을 표시할 수 없습니다. 제출은 config 이름으로
              진행됩니다.
            </EmptyState>
          ) : (
            <EmptyState mode="plain">config를 선택하면 변경 사항이 표시됩니다.</EmptyState>
          )}
        </div>
      ) : model.originalYaml === model.effectiveYaml ? (
        // 차이가 없으면 에디터가 모든 구간을 접어서(hideUnchangedRegions) 빈 판처럼 보인다. 시드
        // override 값이 원본과 같은 상태, 즉 config 를 막 고른 직후가 흔히 그렇다. 접힌 판 대신
        // 아무것도 바뀌지 않았다는 사실을 글로 표시한다. 두 값 모두 normalizeYaml 을 통과한
        // 문자열이라 문자열 비교로 충분하다(configRenderModel).
        <div className={DIFF_STATE_FRAME}>
          <EmptyState mode="plain">원본과 같습니다. 아직 바꾼 값이 없습니다.</EmptyState>
        </div>
      ) : (
        <div className={`${styles.configEditorShell} ${styles.configEditorFill}`}>
          <CodeDiffViewer
            original={model.originalYaml}
            modified={model.effectiveYaml}
            language="yaml"
          />
        </div>
      )}
    </>
  );
}

// 프리필로 승계한 세트를 쓸 수 없어서 비웠을 때만 표시한다. 사용자가 고르지 않은 값이 사라졌으므로
// 알리지 않으면 사용자는 값이 빈 이유를 알 수 없다.
function AnnotationSetDropNotice({ controller }: StepProps): ReactNode {
  const { droppedAnnotationSetCount } = controller;
  if (droppedAnnotationSetCount === 0) {
    return null;
  }
  return (
    <p className={styles.annotationDropNotice}>
      <Text variant={hatcheryTextModes["caption-normal"]}>
        승계한 Annotation Set을 지금은 쓸 수 없어 입력 {droppedAnnotationSetCount}건에서 비웠습니다.
        다시 고르거나 세트를 렌더해 주세요.
      </Text>
    </p>
  );
}

// Switch 형태의 노브. ToggleField 와 짝이고 컨트롤만 다르다. 배치를 고르는 자리처럼 켬/끔이
// 즉시 읽혀야 하는 축에 쓴다. 상태에 따라 결과가 달라지므로 힌트도 함께 바뀐다.
function SwitchField({
  label,
  checked,
  onHint,
  offHint,
  onChange,
}: {
  label: string;
  checked: boolean;
  onHint: string;
  offHint: string;
  onChange: (checked: boolean) => void;
}): ReactNode {
  return (
    <div className={styles.toggleField}>
      <Field
        label={label}
        layout="hug"
        labelPlacement="end"
        textVariant={hatcheryTextModes["body-normal"]}
      >
        <Switch
          checked={checked}
          aria-label={label}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        />
      </Field>
      <p className={styles.hint}>{checked ? onHint : offHint}</p>
    </div>
  );
}

// 채널 선택 요약. topic 별 선택 수를 표시한다. 위임(null) topic 은 수가 아니라 "자동"이다. BE 가
// 제출 시점 제안으로 채우므로 "0개 선택"과 구분해서 표시해야 한다.
function formatChannelSelectionSummary(selection: ChannelSelection): string {
  return CHANNEL_SELECTION_TOPICS.map((topic) => {
    const count = selectedChannelCount(selection, topic);
    return `${channelTopicShortLabel(topic)} ${count === null ? "자동" : count}`;
  }).join(" · ");
}

// 입력 한 건 요약. 역할을 표시하고, 지정되었을 때만 비율·학습 타깃을 추가한다. 지정하지 않은
// 비율은 균등이라 "균등"이라고 적지 않는다.
function formatDatasetSummary(
  dataset: SubmitDatasetSelection,
  isTarget: boolean,
  annotationSummary: string | null,
): string {
  const parts = [dataset.label, dataset.role];
  if (dataset.weight !== null) {
    parts.push(`weight ${dataset.weight}`);
  }
  if (isTarget) {
    parts.push("target");
  }
  if (annotationSummary !== null) {
    parts.push(annotationSummary);
  }
  return parts.join(" · ");
}

// 요약은 uuid 가 아니라 이름으로 표시한다. 후보는 이미 받은 값이라 추가 왕복이 없다.
// 후보에서 찾지 못하면(방금 삭제된 세트 등) 그 사실만 표시한다.
function annotationSetSummary(
  annotationSets: AnnotationSetsSource,
  dataset: SubmitDatasetSelection,
): string | null {
  if (dataset.annotationSetUuid === null) {
    return null;
  }
  const found = annotationSets.byDataset
    .get(dataset.uuid)
    ?.find((set) => set.uuid === dataset.annotationSetUuid);
  return found === undefined ? "Annotation Set 지정" : `Annotation ${annotationSetLabel(found)}`;
}

// init 요약. finetune 을 고르지 않은 상태는 제출 게이트가 막지만, 요약에서도 빈 값 대신 빠진
// 항목을 표시한다.
function formatInitSummary(init: SubmitInitState): string {
  if (init.strategy === "scratch") {
    return "scratch";
  }
  if (init.strategy === "retrain") {
    return `retrain · ${init.source.title}`;
  }
  return init.policy === null ? "finetune — Policy 미선택" : `finetune · ${init.policy.name}`;
}

// 스케줄 확정 요약. 반복 축·기간·실행 게이트를 제출 전체 요약과 같은 표기로 추가한다.
// 값 해석은 scheduleDraft 모듈이 소유한다. 여기서 다시 해석하면 게이트와 표기가 어긋난다.
function ScheduleSummaryRows({
  draft,
  workspaceName,
}: {
  draft: ScheduleDraft;
  // Schedule 이 실행될 공용 Workspace 이름. 목록을 아직 받지 못했으면 null.
  workspaceName: string | null;
}): ReactNode {
  const seconds = scheduleIntervalSeconds(draft);
  const recurrence =
    draft.mode === "interval"
      ? seconds === null
        ? "—"
        : intervalLabel(seconds)
      : `${describeCronExpression(draft.cron) ?? (draft.cron.trim() || "—")} (${resolveScheduleTimezone(draft.timezone)})`;
  const minFreeGpus = scheduleMinFreeGpus(draft);
  const gates = [
    draft.skipIfUnchanged ? "새 Commit 있을 때만" : null,
    minFreeGpus.kind === "value" ? `여유 GPU ≥ ${minFreeGpus.value}` : null,
    draft.trackLatestEvalRelease ? "최신 Release 사용" : null,
  ].filter((gate) => gate !== null);
  return (
    <>
      <SummaryRow label="Schedule 이름" value={draft.name.trim() || "—"} />
      <SummaryRow label="공용 Workspace" value={workspaceName ?? "—"} />
      <SummaryRow label="반복" value={recurrence} />
      <SummaryRow
        label="기간"
        // datetime-local 원문의 "T" 는 화면 어휘가 아니다. 사람이 읽는 표기로 바꾼다.
        value={`${draft.startAt.replace("T", " ") || "바로 시작"} ~ ${draft.endAt.replace("T", " ") || "무기한"}`}
      />
      <SummaryRow label="실행 조건" value={gates.length === 0 ? "매번 실행" : gates.join(" · ")} />
    </>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className={styles.summaryRow}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function clampPositiveInt(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? fallback : Math.max(1, parsed);
}

function formatFactor(factor: number): string {
  return Number.isInteger(factor) ? String(factor) : factor.toFixed(2);
}
